import pako from 'pako';

/**
 * Compressed debug data wrapper (matches backend structure)
 */
export interface CompressedData {
  /** Marker to identify compressed data */
  _compressed: true;
  /** Base64-encoded gzipped JSON */
  data: string;
}

/**
 * Type guard to check if data is compressed
 */
export function isCompressedData(data: unknown): data is CompressedData {
  return (
    typeof data === 'object' &&
    data !== null &&
    '_compressed' in data &&
    (data as CompressedData)._compressed === true &&
    'data' in data &&
    typeof (data as CompressedData).data === 'string'
  );
}

/**
 * Decompresses a CompressedData object back to its original form.
 * Uses pako for browser-compatible gzip decompression.
 *
 * @param compressed - The compressed data wrapper
 * @returns The original decompressed object
 */
export function decompressJson<T>(compressed: CompressedData): T {
  // Decode base64 to binary
  const binaryString = atob(compressed.data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Decompress using pako
  const decompressed = pako.ungzip(bytes, { to: 'string' });

  return JSON.parse(decompressed);
}

/**
 * Decompresses debug data if it's compressed, otherwise returns as-is.
 * Convenience function for handling both compressed and uncompressed debug data.
 *
 * @param data - Potentially compressed debug data
 * @returns The decompressed data
 */
export function decompressDebugData<T>(data: T | CompressedData): T {
  if (isCompressedData(data)) {
    return decompressJson<T>(data);
  }
  return data as T;
}
